import { useCallback } from 'react';
import { useConnectionStatusDiagnostics } from '../connection-status/use-connection-status';
import type { ConnectionStatusHistoryEntry } from '../connection-status/connection-status-context';
import { getDeviceMemoryGb, getJsHeapMb } from './renderer-vitals';
import { getWebGLContextStats } from './webgl-context-registry';

/**
 * Diagnostic context gathered from the renderer process. Assembled and consumed
 * entirely in the renderer — never crosses the IPC bridge.
 */
export type RendererDiagnosticContext = {
    jsHeapUsedMb?: number;
    jsHeapTotalMb?: number;
    jsHeapLimitMb?: number;
    sessionDurationSeconds: number;
    hardwareConcurrency: number;
    deviceMemoryGb?: number;
    /** Live WebGL contexts, from the context registry. */
    glContextsLive: number;
    connectionStatus: Record<string, string>;
    connectionStatusHistory: ConnectionStatusHistoryEntry[];
};

export const pageLoadTimestamp = Date.now();

/**
 * Returns a function that snapshots the renderer diagnostic context on demand.
 * Sources its own input (connection status) via a hook so callers don't have to
 * thread it in, and reads the live values (heap memory, WebGL contexts, session
 * duration) at call time — so callers invoke the returned function at the moment
 * they need a snapshot (e.g. when a map fatal error fires), not at render time.
 *
 * Feature flags are not included: the episode events that accompany every map
 * failure carry the map-relevant ones.
 */
export function useRendererDiagnosticContext(): () => RendererDiagnosticContext {
    const { status, history } = useConnectionStatusDiagnostics();

    return useCallback((): RendererDiagnosticContext => {
        const heap = getJsHeapMb();

        const result: RendererDiagnosticContext = {
            sessionDurationSeconds: Math.round((Date.now() - pageLoadTimestamp) / 1000),
            hardwareConcurrency: navigator.hardwareConcurrency ?? 0,
            glContextsLive: getWebGLContextStats().glContextsLive,
            connectionStatus: { ...status },
            connectionStatusHistory: [...history],
            ...(heap ?? {}),
        };

        const deviceMemory = getDeviceMemoryGb();
        if (deviceMemory != null) {
            result.deviceMemoryGb = deviceMemory;
        }

        return result;
    }, [status, history]);
}
